/**
 * FERP Portal - Governance Context List Page
 *
 * Config type go-: Data governance policies that enforce DRR-114 tier classification,
 * perception-safe naming, retention TTLs, resolver scope requirements, and task field
 * tier restrictions. Evaluated at runtime by governance middleware.
 *
 * NOTE: No GraphQL list query exists for governance contexts yet. Governance configs
 * are loaded at gateway startup. This page shows the known seeded policies.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays known config metadata
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconInfoCircle, IconLock, IconShieldCheck } from '@tabler/icons-react';

const KNOWN_POLICIES = [
  {
    id: 'data-classification',
    name: 'Data Classification Policy',
    description: 'T1-T4 tier classification rules. T1 attestations are positive-only and indefinite. T4 raw sensor data auto-purges via TTL.',
    category: 'classification',
    drrRef: 'DRR-114',
  },
  {
    id: 'entity-ttl-policy',
    name: 'Entity TTL Policy',
    description: 'Auto-purge TTLs for T4 raw sensor records (BLE proximity, wearable captures). 7-30 day retention windows.',
    category: 'retention',
    drrRef: 'DRR-114, DRR-087',
  },
  {
    id: 'perception-safe-naming',
    name: 'Perception-Safe Naming Policy',
    description: 'Terminology mapping for external-facing UI — ensures all labels use perception-safe phrasing per DRR-114.',
    category: 'naming',
    drrRef: 'DRR-114',
  },
  {
    id: 'resolver-scope-policy',
    name: 'Resolver Scope Policy',
    description: 'RequireScope guard mapping for GraphQL resolvers. T3 QAPI data requires qapi:view scope. Resolver-level enforcement.',
    category: 'access',
    drrRef: 'DRR-102, DRR-114',
  },
  {
    id: 'retention-policy',
    name: 'Retention Policy',
    description: 'Data retention windows by tier: T1 indefinite, T2 1 year, T3 90 days (QAPI scope-gated), T4 7-30 days (auto-purge).',
    category: 'retention',
    drrRef: 'DRR-114',
  },
  {
    id: 'task-field-tiers',
    name: 'Task Field Tier Policy',
    description: 'Field-level tier classification for Task primitives. Duration variance is T3 (QAPI-only). Staffing ratios are T3.',
    category: 'classification',
    drrRef: 'DRR-114',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  classification: 'blue',
  retention: 'orange',
  naming: 'teal',
  access: 'red',
};

export function GovernanceListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Governance Policies</Title>
            <Text c="dimmed" size="sm">
              Config type go- — data governance policies enforcing DRR-114 tier classification and DRR-128 write-time minimization
            </Text>
          </div>
          <Badge variant="light" color="red" size="lg">
            {KNOWN_POLICIES.length} Policies
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="No Live GraphQL Query">
          Governance policies are loaded at gateway startup and enforced by middleware.
          A backend list resolver for go- config records is not yet implemented. The cards
          below reflect the current seeded governance configuration.
        </Alert>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="red">
              <IconShieldCheck size={18} />
            </ThemeIcon>
            <Text fw={600}>How Governance Works</Text>
          </Group>
          <Text size="sm" c="dimmed">
            YAML configs → gateway startup load → middleware evaluation on every resolver call.
            Classification determines retention (TTL), access (RequireScope), and naming
            (perception-safe labels). 8-layer enforcement: DDB condition → IAM → code gate → pre-commit → CI → LLM.
          </Text>
        </Card>

        <Stack gap="sm">
          {KNOWN_POLICIES.map((policy) => (
            <Card key={policy.id} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <ThemeIcon variant="light" color={CATEGORY_COLORS[policy.category] ?? 'gray'} size="sm">
                    <IconLock size={14} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{policy.name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{policy.id}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Badge variant="light" color={CATEGORY_COLORS[policy.category] ?? 'gray'} size="sm">
                    {policy.category}
                  </Badge>
                  <Badge variant="outline" size="xs" color="gray" ff="monospace">
                    {policy.drrRef}
                  </Badge>
                </Group>
              </Group>
              <Text size="sm" c="dimmed">{policy.description}</Text>
            </Card>
          ))}
        </Stack>

        <Text size="xs" c="dimmed">
          To modify governance policies, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/governance/*.yaml
          </Anchor>{' '}
          and re-seed.
        </Text>
      </Stack>
    </Container>
  );
}

export default GovernanceListPage;
